import { createStore } from 'vuex'
import { authAPI } from '@/services/api.js'
import {
  setToken,
  setRefreshToken,
  setUserInfo,
  clearAuth,
  getUserInfo,
  isLoggedIn as checkIsLoggedIn
} from '@/utils/auth.js'

// 从 localStorage 恢复用户权限数据
const storedUserInfo = getUserInfo() || {}

export default createStore({
  state: {
    isLoggedIn: checkIsLoggedIn(),
    user: storedUserInfo,
    permissions: storedUserInfo.permissions || [],
    menus: storedUserInfo.menus || [],
    buttons: storedUserInfo.buttons || [],
    devices: [],
    alerts: [],
    monitoringItems: []
  },
  getters: {
    isAuthenticated: state => state.isLoggedIn,
    currentUser: state => state.user,
    userPermissions: state => state.permissions,
    userMenus: state => state.menus,
    userButtons: state => state.buttons,
    isSuperuser: state => state.user?.is_superuser || false,
    deviceCount: state => state.devices.length,
    activeAlertCount: state => state.alerts.filter(alert => alert.status === 'active').length
  },
  mutations: {
    SET_LOGIN_STATE(state, isLoggedIn) {
      state.isLoggedIn = isLoggedIn
    },
    SET_USER(state, user) {
      state.user = user
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions
    },
    SET_MENUS(state, menus) {
      state.menus = menus
    },
    SET_BUTTONS(state, buttons) {
      state.buttons = buttons
    },
    CLEAR_AUTH(state) {
      state.isLoggedIn = false
      state.user = {}
      state.permissions = []
      state.menus = []
      state.buttons = []
    },
    logout(state) {
      state.isLoggedIn = false
      state.user = {}
      state.permissions = []
      state.menus = []
      state.buttons = []
    },
    setDevices(state, devices) {
      state.devices = devices
    },
    setAlerts(state, alerts) {
      state.alerts = alerts
    },
    setMonitoringItems(state, items) {
      state.monitoringItems = items
    }
  },
  actions: {
    // 登录
    async login({ commit }, credentials) {
      try {
        const response = await authAPI.login(credentials)
        const result = response.data

        if (result.success) {
          // 后端返回的数据在 result.data 中
          const data = result.data
          
          // 保存Token
          setToken(data.access_token)
          setRefreshToken(data.refresh_token)

          // 保存用户信息
          const userInfo = data.user
          setUserInfo(userInfo)

          // 更新状态
          commit('SET_LOGIN_STATE', true)
          commit('SET_USER', userInfo)
          commit('SET_PERMISSIONS', userInfo.permissions || [])
          commit('SET_MENUS', userInfo.menus || [])
          commit('SET_BUTTONS', userInfo.buttons || [])

          return { success: true, data: result }
        } else {
          return { success: false, message: result.message }
        }
      } catch (error) {
        const message = error.response?.data?.detail || error.message || '登录失败'
        return { success: false, message }
      }
    },

    // 登出
    async logout({ commit }) {
      try {
        await authAPI.logout()
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        // 清除认证信息
        clearAuth()
        commit('CLEAR_AUTH')
      }
    },

    // 获取当前用户信息
    async getUserInfo({ commit }) {
      try {
        const response = await authAPI.getMe()
        const data = response.data

        if (data.success) {
          const userInfo = data.data
          setUserInfo(userInfo)

          commit('SET_USER', userInfo)
          commit('SET_PERMISSIONS', userInfo.permissions || [])
          commit('SET_MENUS', userInfo.menus || [])
          commit('SET_BUTTONS', userInfo.buttons || [])

          return { success: true, data: userInfo }
        } else {
          return { success: false, message: data.message }
        }
      } catch (error) {
        const message = error.response?.data?.detail || error.message || '获取用户信息失败'
        return { success: false, message }
      }
    },

    // 修改密码
    async changePassword(_, passwordData) {
      try {
        const response = await authAPI.changePassword(passwordData)
        return { success: true, data: response.data }
      } catch (error) {
        const message = error.response?.data?.detail || error.message || '修改密码失败'
        return { success: false, message }
      }
    }
  }
})
